// src/utils/featureFlags.ts
//
// FS-496: app feature flags. Wire shapes + input helpers for the Features
// admin page. Flags live in Firestore behind firebase-api; every mutation
// triggers a server-side batch that rewrites the `flags` name list stored on
// each affected app user's record (names only — never the flag definition).

/**
 * A flag's condition, evaluated per app user. `user_ids` is the only type in
 * v1; group/customer targeting later means adding types here, not replacing
 * the mechanism.
 */
export interface FlagCondition {
  type: 'user_ids';
  /** Firestore user IDs the flag is on for. */
  user_ids: string[];
}

export interface FeatureFlag {
  name: string;
  condition: FlagCondition;
  updated_at?: string;
}

/**
 * Response to create/update/delete. `users_updated` counts the app user
 * records whose stored flag list changed in the batch rewrite.
 */
export interface FlagMutationResponse {
  flag?: FeatureFlag;
  users_updated?: number;
  /** IDs in the condition that matched no app user record — typos to fix. */
  unknown_user_ids?: string[];
}

/** App code checks flags by literal name — keep names machine-safe. */
export const FLAG_NAME_REGEX = /^[a-z][a-z0-9_]{0,63}$/;

/**
 * Parse the admin's pasted ID list. Comma-separated per the spec, but
 * newlines and stray whitespace also split so a column pasted from a
 * spreadsheet works. De-duplicates, preserving first-seen order.
 */
export const parseUserIdsInput = (raw: string): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[\s,]+/)) {
    if (part && !seen.has(part)) {
      seen.add(part);
      out.push(part);
    }
  }
  return out;
};
